import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Heading, Label, Paragraph, Select, Tag } from '@digdir/designsystemet-react';
import { EyeSlashIcon, PlusIcon } from '@navikt/aksel-icons';
import type { MeasureStep, Proposal, MeasureArea } from '../../../api/workEnvironmentTypes';
import { proposalOrigin, proposalsIn, ranksByHighlights } from '../measuresView';
import { areaName, dayAndTime } from '../riskView';
import { HideProposalDialog } from './HideProposalDialog';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = {
  surveyId: number;
  step: MeasureStep;
  areas: MeasureArea[];
  proposals: Proposal[];
  onAdd: () => void;
};

export const ProposalsCard = ({ surveyId, step, areas, proposals, onAdd }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const [areaKey, setAreaKey] = useState<string | null>(null);
  const [hiding, setHiding] = useState<Proposal | null>(null);
  const shown = proposalsIn(proposals, areaKey);
  const ranked = ranksByHighlights(step);
  const nameOf = (key: string) => {
    const area = areas.find((candidate) => candidate.key === key);
    return area ? areaName(area, t) : key;
  };

  return (
    <section aria-labelledby="proposals-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.cardHeading}>
          <div className={process.sideHeading}>
            <Heading level={2} data-size="sm" id="proposals-heading">
              {t('workEnvironmentMeasures.proposals.title')}
            </Heading>
            <Paragraph data-size="sm" className={process.subtle}>
              {t(`workEnvironmentMeasures.proposals.intro.${step}`)}
            </Paragraph>
          </div>
          {step === 'propose' && may('propose') && (
            <Button variant="secondary" data-size="sm" onClick={onAdd}>
              <PlusIcon aria-hidden />
              {t('workEnvironmentMeasures.leader.add')}
            </Button>
          )}
        </div>
        <Field data-size="sm" className={classes.filterField}>
          <Label htmlFor="proposals-area">{t('workEnvironmentMeasures.proposals.filter')}</Label>
          <Select
            id="proposals-area"
            value={areaKey ?? ''}
            onChange={(event) => setAreaKey(event.target.value || null)}
          >
            <Select.Option value="">{t('workEnvironmentMeasures.proposals.allAreas')}</Select.Option>
            {areas.map((area) => (
              <Select.Option key={area.key} value={area.key}>
                {t('workEnvironmentMeasures.areaOption', {
                  name: areaName(area, t),
                  level: t(`workEnvironmentRisk.matrix.levels.${area.level}`).toLowerCase(),
                })}
              </Select.Option>
            ))}
          </Select>
        </Field>
        <Paragraph data-size="sm" className={process.subtle} aria-live="polite">
          {t('workEnvironmentMeasures.proposals.count', { count: shown.length })}
        </Paragraph>
        <ol className={classes.proposalList}>
          {shown.map((proposal, index) => (
            <li key={proposal.id} className={classes.proposal} data-hidden={Boolean(proposal.hidden)}>
              {ranked && (
                <span className={`ds-heading ${classes.rank}`} data-size="xs" aria-hidden="true">
                  {index + 1}
                </span>
              )}
              <span className={classes.areaTitle}>
                <span className="ds-label">{proposal.title}</span>
                {proposal.reason && (
                  <span className="ds-paragraph" data-size="sm">
                    {proposal.reason}
                  </span>
                )}
                <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                  {nameOf(proposal.factorKey)} · {proposalOrigin(proposal, t)} ·{' '}
                  {dayAndTime(proposal.createdAt, t, i18n.language)}
                </span>
                {proposal.hidden && (
                  <span className="ds-paragraph" data-size="sm">
                    {t('workEnvironmentMeasures.proposals.hidden', { reason: proposal.hidden.reason })}
                  </span>
                )}
              </span>
              <span className={classes.areaTags}>
                {proposal.hidden && (
                  <Tag data-size="sm" data-color="neutral">
                    {t('workEnvironmentMeasures.proposals.hiddenTag')}
                  </Tag>
                )}
                {ranked && !proposal.hidden && (
                  <span className={classes.highlightCount}>
                    <span className="ds-heading" data-size="xs">
                      {proposal.highlights}
                    </span>
                    <span className="ds-paragraph" data-size="xs">
                      {t('workEnvironmentMeasures.proposals.highlights', { count: proposal.highlights })}
                    </span>
                  </span>
                )}
                {!proposal.hidden && step !== 'plan' && may('hideProposal') && (
                  <Button
                    variant="tertiary"
                    data-size="sm"
                    icon
                    aria-label={t('workEnvironmentMeasures.hide.action', { title: proposal.title })}
                    onClick={() => setHiding(proposal)}
                  >
                    <EyeSlashIcon aria-hidden />
                  </Button>
                )}
              </span>
            </li>
          ))}
        </ol>
      </div>
      {hiding && <HideProposalDialog surveyId={surveyId} proposal={hiding} onClose={() => setHiding(null)} />}
    </section>
  );
};
